import type React from "react";
import { useCallback } from "react";

/**
 * 年別選択が可能な地図レイヤーを制御するためのラッパーコンポーネント
 *
 * overlayVisibility（全レイヤーの表示状態）の中からnameで指定されたレイヤー名を持つレイヤーを探し
 * 値がtrueの場合はrenderを呼び出し、該当のレイヤーを描画する
 * 年度切りかえについては、レイヤー名の後ろに_2024などのサフィックスが追加されている前提で動作します
 *
 * 例えばFLOOD_10でoverlayVisibility[]が下記のようになっている場合は
 * 「FLOOD_10全体が有効でその中から2024年を表示する」という意味になります
 * FLOOD_10 : true
 * FLOOD_10_2024 : true
 * FLOOD_10_2025 : false
 */
interface Props {
  overlayVisibility: Record<string, boolean>;
  name: string;
  render: (
    year: number,
    defaultVisible: boolean,
    visible: boolean
  ) => React.ReactElement;
}

const YearSelectableLayer: React.FC<Props> = (
  props
): React.ReactElement | null => {
  const { render, overlayVisibility, name } = props;

  /**
   * 現在有効な年の数値をoverlayVisibilityの中から取り出す
   * overlayVisibilityには"FLOOD_10_2024"のような形で"_"区切りの年が書かれている想定です。その中から最後の2024の部分だけ取り出す
   * @param prefix 年を取り出したいレイヤー名の接頭辞（例: "FLOOD_10"）
   * @return {number | null} 有効になっている年(本来ならあり得ないが、複数ある場合は最初に見つかった値)、存在しない場合はnull
   */
  const getActiveYear = useCallback(
    (prefix: string): number | null => {
      // 対象のレイヤー全体がtrueになっているか確認
      const isLayerActive = Boolean(overlayVisibility[prefix]);
      if (!isLayerActive) return null;

      // `${prefix}_`から始まるkeyを探す。見つからない場合はnullを返す
      const keys = Object.keys(overlayVisibility);
      const layerNames = keys.filter((key) => key.startsWith(`${prefix}_`));
      if (layerNames.length === 0) return null;

      // `${prefix}_`から始まる要素の中から値がtrueになっている最初の要素を一つ取り出す
      const enabledYear = layerNames.find((value) => overlayVisibility[value]);
      if (enabledYear === undefined) return null;

      // 最後の'_'の後ろは年数がかかれている想定 例: _2024
      const m = enabledYear.match(/_(\d{4})$/);
      if (!m) {
        return null;
      }
      const year = Number(m[1]);

      if (!Number.isInteger(year) || year <= 0) {
        return null;
      }

      return year;
    },
    [overlayVisibility]
  );

  const year = getActiveYear(name);

  // yearに年が入っている場合はrenderを呼び出してタイルを描画する
  return year !== null ? render(year, true, true) : null;
};

export default YearSelectableLayer;
